import type {
  QuestionnaireAnswers,
  QuestionnaireProfile,
  QuestionnaireQuestion,
  QuestionnaireSubmission,
  QuestionnaireAudience,
  QuestionnaireVersion,
} from './questionnaires';
export type EventPaymentMethod =
  | 'stripe'
  | 'captain_interac'
  | 'individual_interac';
export type EventStage = 'Not Open' | 'Open' | 'Payment' | 'Archived';
export interface EventDetailsDraft {
  name: string;
  description: string;
  startsAt: string | null;
  endsAt: string | null;
  location: string;
  address: string;
  amountCents: number;
  paymentMethod: EventPaymentMethod;
  paymentEmail: string;
  eventCode: string;
  seasonId: string | null;
  audience: QuestionnaireAudience;
  selectedMemberIds: string[];
}
export interface ManagedEventDetails extends Omit<
  EventDetailsDraft,
  'amountCents'
> {
  amountCents: number | null;
  stage: EventStage;
  locked: boolean;
  responseCount: number;
  registrationCount: number;
  paidCount: number;
}
export interface EventFormVersion extends QuestionnaireVersion {
  publishedAt: string;
}
/** Stored as JSON in `Events.Form`. Members answer only the published version; it is null until the event has a question. */
export interface EventForm {
  revision: number;
  audience: QuestionnaireAudience;
  selectedMemberIds: string[];
  questions: QuestionnaireQuestion[];
  published: EventFormVersion | null;
}
/** Airtable-authored events have no form; `Visibility` and `Selected Members` gate them. */
export interface ManagedEvent {
  id: string;
  details: ManagedEventDetails;
  form: EventForm | null;
}
/** One portal save of an event; `changes` holds only the fields it changed. */
export interface EventHistoryEntry {
  at: string;
  summary: string;
  changes: Partial<Record<EventSaveField, { before: unknown; after: unknown }>>;
}
/** A field an `EventFormSave` sets. */
export type EventSaveField = keyof EventDetailsDraft | 'questions';
export interface EventFormSave {
  revision: number;
  details: EventDetailsDraft;
  questions: QuestionnaireQuestion[];
}
/** A team's running balance: owed is its RSVPs × price, received is what staff recorded. Null when Airtable holds an unreadable price or amount. */
export interface EventTeamSummary {
  id: string;
  name: string;
  attendees: { id: string; registrationId: string; name: string }[];
  owedCents: number | null;
  receivedCents: number | null;
  balanceCents: number | null;
  paid: boolean;
  /** Null when the event lacks an event code or a valid recipient. */
  payment: { recipient: string; memo: string } | null;
}

export type EventAction =
  | {
      kind: 'register';
      teamId?: string;
      questionnaire?: QuestionnaireSubmission;
    }
  | { kind: 'cancel' }
  | { kind: 'edit'; questionnaire: QuestionnaireSubmission };

/** The member's latest answers, with the questions of the form version they answered. */
export interface MemberEventResponse {
  questions: QuestionnaireQuestion[];
  answers: QuestionnaireAnswers;
}

export interface PortalEvent {
  id: string;
  name: string;
  description: string;
  infoUrl: string | null;
  surveyUrl?: string | null;
  questionnaire: QuestionnaireVersion | null;
  questionnaireProfile?: QuestionnaireProfile;
  startsAt: string | null;
  endsAt?: string | null;
  location: string;
  address: string;
  audience:
    | 'Public'
    | 'Members'
    | 'Staff'
    | 'Selected members'
    | 'FTLO members'
    | 'Current season players';
  paymentMethod?: EventPaymentMethod;
  registrationOpen: boolean;
  availability: 'open' | 'closed' | 'full' | 'unavailable';
  registered: boolean;
  paid: boolean;
  amountCents: number | null;
  attendance: {
    verified: number;
    pending: number;
    capacity: number | null;
  } | null;
  teamRegistration?: {
    season: string;
    selectedTeamId: string | null;
    teams: { id: string; name: string }[];
    captainTeams: EventTeamSummary[];
  };
  payment:
    | { method: 'interac'; recipient: string; memo: string }
    | { method: 'stripe'; checkoutUrl: string | null }
    | null;
}

export const EVENT_COPY = {
  eventInfo: 'Event info',
  detailsButton: 'Details',
  yourAnswers: 'Your answers',
  saveAnswers: 'Save answers',
  answersSaved: 'Your answers are saved.',
  answersFailed: 'We couldn’t save your answers. Please try again.',
  detailsLoading: 'Loading your answers…',
  paymentPending: 'Payment pending',
  paymentReceived: 'Payment received',
  pricePerPerson: (amount: string) => `${amount} per person`,
  transferAmount: 'Amount',
  transferEmail: 'To',
  transferMemo: 'Memo',
  rsvp: 'RSVP',
  cancelRsvp: 'Cancel RSVP',
  rsvpd: 'RSVP’d',
  registered: 'Registered',
  paid: 'Paid',
  cancelTitle: 'Cancel your RSVP?',
  cancelBody: (event: string) =>
    `You’ll give up your spot at ${event}. Anything you’ve already paid isn’t refunded.`,
  keepSpot: 'Keep my spot',
  captainSummaryTitle: 'Your team’s RSVPs',
  noAttendees: 'No one from your team has RSVP’d yet.',
  invalidTeam:
    'We couldn’t match you to a team for this event’s season. Please contact staff.',
  cancellationFailed: 'We couldn’t cancel your RSVP. Please try again.',
  title: 'Events',
  homeDescription: 'Socials and other club events',
  upcoming: 'Upcoming events',
  empty: 'No events to show yet.',
  loading: 'Loading events…',
  register: 'Register',
  viewAndRegister: 'View & register',
  allEvents: 'All events',
  verifiedFree: 'Registration confirmed.',
  closed: 'Registration closed',
  full: 'Event full',
  calendar: 'Add to Google Calendar',
  calendarFile: 'Download calendar file',
  free: 'Free',
  unlimited: 'No capacity limit',
  memoWarning:
    'Paste the memo exactly as shown. If the memo is missing or inaccurate, it’ll take longer to verify your payment.',
  verificationInstructions:
    'Your registration is pending until we confirm your e-transfer.',
  unavailable: 'This event is unavailable.',
  registrationClosed: 'Registration for this event is closed.',
  registrationFull: 'This event is full.',
  missingName: 'Add your name to your profile before registering.',
  paymentNotReady:
    'Registration for this event is not ready yet. Please try again later.',
  registrationFailed: 'We couldn’t register you. Please try again.',
  registrationNeedsReview:
    'We couldn’t confirm your registration details. Please contact staff.',
  invalidCache: 'Cached event details could not be read.',
} as const;

export function eventInfoUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
